import React, { useEffect, useRef, useState } from "react";
import { getStoredTheme } from "../lib/theme";

/**
 * TradingView Advanced Chart widget embed.
 * Uses explicit pixel dimensions (NOT autosize) — autosize sometimes reads the
 * container before its real height is applied, leaving a tiny iframe.
 * Re-mounts on theme change.
 */
export default function TradingViewChart({ symbol = "OANDA:XAUUSD", height = 480 }) {
  const containerRef = useRef(null);
  const [theme, setTheme] = useState(getStoredTheme());
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const handler = (e) => setTheme(e.detail.theme);
    window.addEventListener("ft:theme-change", handler);
    return () => window.removeEventListener("ft:theme-change", handler);
  }, []);

  // Track container width (so we can pass explicit width to TV)
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const measure = () => setWidth(Math.floor(node.getBoundingClientRect().width));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || !width) return;
    node.innerHTML = "";

    const widgetDiv = document.createElement("div");
    widgetDiv.className = "tradingview-widget-container__widget";
    widgetDiv.style.height = `${height}px`;
    widgetDiv.style.width = `${width}px`;
    node.appendChild(widgetDiv);

    const isDark = theme !== "light";
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.async = true;
    script.type = "text/javascript";
    script.innerHTML = JSON.stringify({
      width: width,
      height: height,
      symbol,
      interval: "D",
      timezone: "Europe/Berlin",
      theme: isDark ? "dark" : "light",
      style: "1",
      locale: "de_DE",
      toolbar_bg: isDark ? "#0a0a0a" : "#ffffff",
      enable_publishing: false,
      hide_top_toolbar: false,
      hide_legend: false,
      withdateranges: true,
      allow_symbol_change: true,
      save_image: false,
      backgroundColor: isDark ? "rgba(9, 9, 11, 1)" : "rgba(255, 255, 255, 1)",
      gridColor: isDark ? "rgba(39, 39, 42, 0.5)" : "rgba(228, 228, 231, 0.6)",
      support_host: "https://www.tradingview.com",
    });
    node.appendChild(script);
  }, [symbol, height, theme, width]);

  return (
    <div
      ref={containerRef}
      className="tradingview-widget-container border border-zinc-800 bg-zinc-950"
      data-testid="tradingview-chart"
      style={{ height: `${height}px`, minHeight: `${height}px`, width: "100%" }}
    />
  );
}
